import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { SettingsProvider } from './contexts/SettingsContext.tsx';

// Pages
import Login from './pages/Login.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Assets from './pages/Assets.tsx';
import CreateAsset from './pages/CreateAsset.tsx';
import AssetDetailNew from './pages/AssetDetailNew.tsx';
import AssetExchange from './pages/AssetExchange.tsx';
import AssetSale from './pages/AssetSale.tsx';
import EditAsset from './pages/EditAsset.tsx';
import Vehicles from './pages/Vehicles.tsx';
import Vessels from './pages/Vessels.tsx';
import CreateVehicle from './pages/CreateVehicle.tsx';
import CreateVessel from './pages/CreateVessel.tsx';
import VehicleDetail from './pages/VehicleDetail.tsx';
import VesselDetail from './pages/VesselDetail.tsx';
import VehicleSale from './pages/VehicleSale.tsx';
import VesselSale from './pages/VesselSale.tsx';
import EditVehicle from './pages/EditVehicle.tsx';
import EditVessel from './pages/EditVessel.tsx';
import Aircrafts from './pages/Aircrafts.tsx';
import CreateAircraft from './pages/CreateAircraft.tsx';
import AircraftDetail from './pages/AircraftDetail.tsx';
import EditAircraft from './pages/EditAircraft.tsx';
import AircraftSale from './pages/AircraftSale.tsx';
import Reports from './pages/Reports.tsx';
import NewReports from './pages/NewReports.tsx';
import Debts from './pages/Debts.tsx';
import CreateDebt from './pages/CreateDebt.tsx';
import DebtDetail from './pages/DebtDetail.tsx';
import EditDebt from './pages/EditDebt.tsx';
import Receivables from './pages/Receivables.tsx';
import CreateReceivable from './pages/CreateReceivable.tsx';
import ReceivableDetail from './pages/ReceivableDetail.tsx';
import EditReceivable from './pages/EditReceivable.tsx';
import Rentals from './pages/Rentals.tsx';
import CreateRental from './pages/CreateRental.tsx';
import EditRental from './pages/EditRental.tsx';
import UserManagement from './pages/UserManagement.tsx';
import EditUser from './pages/EditUser.tsx';
import UserDetail from './pages/UserDetail.tsx';
import CreateUser from './pages/CreateUser.tsx';
import Whitelabel from './pages/Whitelabel.tsx';
import Companies from './pages/Companies.tsx';
import EditCompany from './pages/EditCompany.tsx';
import CompanyDetail from './pages/CompanyDetail.tsx';
import Investments from './pages/Investments.tsx';
import CreateInvestment from './pages/CreateInvestment.tsx';
import InvestmentDetail from './pages/InvestmentDetail.tsx';
import EditInvestment from './pages/EditInvestment.tsx';
import Philanthropies from './pages/Philanthropies.tsx';
import CreatePhilanthropy from './pages/CreatePhilanthropy.tsx';
import EditPhilanthropy from './pages/EditPhilanthropy.tsx';
import PhilanthropyDetail from './pages/PhilanthropyDetail.tsx';
import ExchangeRegister from './pages/ExchangeRegister.tsx';

// Componentes
import ProtectedRoute from './components/ProtectedRoute.tsx';
import Layout from './components/Layout.tsx';

// Criar instância do QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SettingsProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute requiredResource="dashboard">
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredResource="dashboard">
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Assets />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/create"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateAsset />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <AssetDetailNew />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id/exchange"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <AssetExchange />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/exchanges/new"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <ExchangeRegister />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditAsset />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <AssetSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Rentals */}
              <Route
                path="/rentals"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Rentals />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rentals/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditRental />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id/rentals/new"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <CreateRental />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <ProtectedRoute requiredPermission="users.read">
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/new"
                element={
                  <ProtectedRoute requiredPermission="users.create">
                    <Layout>
                      <CreateUser />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Vehicles />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Vessels */}
              <Route
                path="/vessels"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Vessels />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/edit-vehicle/:id"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditVehicle />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/edit-vessel/:id"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditVessel />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/new"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateVehicle />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vessels/new"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateVessel />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <VehicleDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vessels/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <VesselDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <VehicleSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vessels/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <VesselSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Aircrafts */}
              <Route
                path="/aircrafts"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Aircrafts />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aircrafts/new"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateAircraft />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aircrafts/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <AircraftDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/edit-aircraft/:id"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditAircraft />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aircrafts/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <AircraftSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Companies */}
              <Route
                path="/companies"
                element={
                  <ProtectedRoute requiredResource="companies">
                    <Layout>
                      <Companies />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/companies/new"
                element={
                  <ProtectedRoute requiredPermission="companies.create">
                    <Layout>
                      <EditCompany />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/companies/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="companies.update">
                    <Layout>
                      <EditCompany />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/companies/:id"
                element={
                  <ProtectedRoute requiredResource="companies">
                    <Layout>
                      <CompanyDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Investments */}
              <Route
                path="/investments"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <Investments />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/investments/create"
                element={
                  <ProtectedRoute requiredPermission="investments.create">
                    <Layout>
                      <CreateInvestment />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/investments/:id"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <InvestmentDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/investments/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="investments.update">
                    <Layout>
                      <EditInvestment />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Philanthropies */}
              <Route
                path="/philanthropies"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <Philanthropies />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/philanthropies/:id"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <PhilanthropyDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/philanthropies/create"
                element={
                  <ProtectedRoute requiredPermission="investments.create">
                    <Layout>
                      <CreatePhilanthropy />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/philanthropies/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="investments.update">
                    <Layout>
                      <EditPhilanthropy />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/debts"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <Debts />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/receivables"
                element={
                  <ProtectedRoute requiredResource="receivables">
                    <Layout>
                      <Receivables />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/receivables/new"
                element={
                  <ProtectedRoute requiredPermission="receivables.create">
                    <Layout>
                      <CreateReceivable />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/receivables/:id"
                element={
                  <ProtectedRoute requiredResource="receivables">
                    <Layout>
                      <ReceivableDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/receivables/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="receivables.update">
                    <Layout>
                      <EditReceivable />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/debts/new"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <CreateDebt />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/debts/:id"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <DebtDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/debts/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="debts.update">
                    <Layout>
                      <EditDebt />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports"
                element={
                  <ProtectedRoute requiredResource="reports">
                    <Layout>
                      <Reports />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/new-reports"
                element={
                  <ProtectedRoute requiredResource="reports">
                    <Layout>
                      <NewReports />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/edit/:id"
                element={
                  <ProtectedRoute requiredResource="users" requiredPermission="users.update">
                    <EditUser />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/:id"
                element={
                  <ProtectedRoute requiredResource="users" requiredPermission="users.read">
                    <Layout>
                      <UserDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Navigate to="/users/edit/current" replace />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/whitelabel"
                element={
                  <ProtectedRoute
                    requiredResource="whitelabel"
                    requiredPermission="whitelabel.update"
                  >
                    <Layout>
                      <Whitelabel />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Redirecionar para dashboard se a rota não existir */}
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
